import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Header, SpaceBetween } from "@amzn/awsui-components-react";
import TPPolarisTable from "src/components/shared/TPPolarisTable";
import ServiceCollection from "src/services/ServiceCollection";
import apiService from "src/services/ApiCallService";
import { TestedPartyMapping } from "src/models/tested-party-mapping/TestedPartyMapping";
import EditTestedPartyMappingModal from "./EditTestedPartyMappingModal";
import {
    PAGE_SIZE_OPTIONS,
    DEFAULT_PREFERENCES,
    VISIBLE_CONTENT_OPTIONS,
    mappingsColumnDefinitions,
    filteringProperties,
    CONFIG_TYPE_TESTED_PARTY,
} from "./Constants";

/** Displays all CLI-to-Tested-Party mappings in a filterable, sortable table with inline editing. */
export default function CLIReportingConfigMapping(props: { services: ServiceCollection; preSelectedCli?: string }) {
    const [mappings, setMappings] = useState<TestedPartyMapping[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedItems, setSelectedItems] = useState<TestedPartyMapping[]>([]);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [reloadSignal, setReloadSignal] = useState(0);
    const autoSelectDone = useRef(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                const response = await apiService.getAllCliReportingConfig(CONFIG_TYPE_TESTED_PARTY);

                if (!cancelled) {
                    const records: TestedPartyMapping[] = response?.CliReportingConfigs ?? [];
                    setMappings(records);
                    // Auto-select the row and open edit modal if navigated with ?cli=<cliNumber>
                    if (props.preSelectedCli && !autoSelectDone.current) {
                        const match = records.find(m => m.cliNumber === props.preSelectedCli);
                        if (match) {
                            setSelectedItems([match]);
                            setEditModalVisible(true);
                            autoSelectDone.current = true;
                        }
                    }
                }
            } catch (error: any) {
                if (!cancelled) {
                    const msg = error?.message || String(error);
                    props.services.messageService.showErrorAutoDismissBanner("Failed to load tested party mappings: " + msg);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        })();
        return () => { cancelled = true; };
    }, [reloadSignal, props.preSelectedCli]);

    const selectedMapping = selectedItems[0] ?? null;

    const tableHeader = (
        <Header
            actions={
                <SpaceBetween direction="horizontal" size="xs">
                    <Button
                        disabled={!selectedMapping}
                        onClick={() => setEditModalVisible(true)}
                    >
                        Edit
                    </Button>
                </SpaceBetween>
            }
            counter={mappings.length ? `(${mappings.length})` : undefined}
        >
            Tested Party Mappings
        </Header>
    );

    return (
        <SpaceBetween direction="vertical" size="m">
            <TPPolarisTable
                sortingColumn="cliNumber"
                header={tableHeader}
                tableEmptyState={<Box textAlign="center">No tested party mappings to display</Box>}
                columnDefinitions={mappingsColumnDefinitions}
                tableItems={mappings}
                defaultPreferences={DEFAULT_PREFERENCES}
                preferencesStorageKey="testedPartyMappingsTablePreferences"
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                visibleContentOptions={VISIBLE_CONTENT_OPTIONS}
                filteringEmptyText="No mappings found"
                filteringLoadingText="Loading mappings"
                filteringProperties={filteringProperties}
                loadingText="Loading tested party mappings"
                resizableColumns={true}
                isLoading={loading}
                selectionType="single"
                selectedItems={selectedItems}
                setSelectedItems={({ detail }) => setSelectedItems(detail.selectedItems)}
                trackBy="cliNumber"
            />

            <EditTestedPartyMappingModal
                visible={editModalVisible}
                mapping={selectedMapping}
                services={props.services}
                onDismiss={() => setEditModalVisible(false)}
                onSuccess={() => {
                    setEditModalVisible(false);
                    setSelectedItems([]);
                    setReloadSignal((n) => n + 1);
                }}
            />
        </SpaceBetween>
    );
}
